/**
 * RevealPhaseShell, M4 batch 6c (reworked: reveal is an OVERLAY, not a swap).
 *
 * Renders for `phase === "reveal"`. The turn just ended; we reveal the canonical
 * word, the drawer + tier, drawer points, the ordered correct-guessers, a running
 * cumulative scoreboard, and a countdown to the next turn (or gameOver).
 *
 * IMPORTANT (the "don't hide anything" fix): reveal reuses the SAME 3-column game
 * frame the drawing phase uses (`.drawing-phase-shell`: players panel · canvas ·
 * chat feed): mirroring how `WaitingForDrawer` reuses it for wordPick. Nothing is
 * hidden between drawing and reveal; the players panel and the guess feed stay put,
 * the kept drawing stays at the SAME size, and the reveal summary is an OVERLAY
 * (an opaque --paper card on a >=80% --paper scrim) centered over the canvas. This
 * also makes the prior "keep the canvas size" fix structural, the canvas is the
 * drawing phase's own `.canvas-wrapper`, so it cannot resize between phases.
 *
 * Data source: the last entry of `state.game.turnSummaries` (just-appended by the
 * reducer at PHASE_END(drawing → reveal)). Player names + colors come from
 * `state.players`. The cumulative scoreboard reuses the SAME pure
 * `aggregateScoreboard` the gameOver board uses (3-strike rule: no second path).
 *
 * Aborted-reason narration mirrors PRD §3.8:
 *   - `drawer-disconnect` → "Drawer left, no points awarded"
 *   - `all-guessed`       → "Everyone guessed!"
 *   - `timeout`           → "Time's up!"
 *   - undefined           → nothing rendered (clean turn end)
 */

import type { SerializedPlayer, TurnSummary } from "@drawlol/shared";
import { DIFFICULTY_MULTIPLIER } from "@drawlol/shared";
import type { GameStateView } from "../ws.js";
import { useDeadlineCountdown } from "../hooks/useDeadlineCountdown.js";
import { BoxFrame } from "../marks/v1/BoxFrame.js";
import { SCRIBBLE_IN_CLASS } from "../anim/scribble-in.js";
import { CountUp } from "../anim/count-up.js";
import { aggregateScoreboard, competitionRanks } from "../game/scoreboard.js";
import { buildTurnScores } from "../game/turnScores.js";
import { PlayersPanel } from "../components/PlayersPanel.js";
import { GuessChatFeed } from "../components/GuessChatFeed.js";

type RevealPhaseShellProps = {
  /** Cached game-state projection from RoomShell. */
  game: GameStateView;
  /** Player roster (id + name + avatar color), drives the left panel + name lookups. */
  players: ReadonlyArray<SerializedPlayer>;
  /** Wall-clock ms remaining in the reveal phase (from snapshot). */
  msRemaining: number;
  /**
   * The kept drawing: RoomShell passes a `<DrawingCanvas readOnly />` that replays
   * the persisted `$finalizedStrokes`. Rendered inside the center column's
   * `.canvas-wrapper` (aria-hidden, inert) UNDER the reveal card. Passed as a render
   * prop so this shell stays unit-testable without canvas getContext/ResizeObserver
   * mocking. Omitted → no canvas layer (the snapshot-race fallback + unit tests).
   */
  canvasSlot?: preact.ComponentChildren;
  /** Local player's id: drives the left panel's "me" highlight. Optional for tests. */
  playerId?: string | null;
  /** Room code: drives the left panel's in-game invite chip. Optional for tests. */
  roomId?: string;
  /**
   * Q9 (audit 2026-06-11): id → name memory for players who left mid-game,
   * keeps their rows on the running standings (grayed, "(left)") instead of
   * silently vanishing between turns. Same map GameOverShell receives.
   */
  departedNames?: ReadonlyMap<string, string>;
};

function abortedReasonText(reason: TurnSummary["abortedReason"]): string | null {
  if (reason === "drawer-disconnect") return "Drawer left — no points awarded";
  if (reason === "all-guessed") return "Everyone guessed!";
  if (reason === "timeout") return "Time's up!";
  return null;
}

/** "Turn N/M" (+ " • Round r/R" only when there's more than one round). */
function headerLabel(game: GameStateView): string {
  const turn = `Turn ${game.currentTurnIndex + 1}/${game.turnOrder.length}`;
  if (game.round <= 1) return turn;
  return `${turn} • Round ${game.currentRound}/${game.round}`;
}

export function RevealPhaseShell({
  game,
  players,
  msRemaining,
  canvasSlot,
  playerId = null,
  roomId = "",
  departedNames,
}: RevealPhaseShellProps) {
  // The just-appended summary is the last entry of turnSummaries. Use optional
  // chaining so a brief snapshot-arrival race doesn't crash; the next
  // ROOM_STATE_SYNC fills it in.
  const summary = game.turnSummaries[game.turnSummaries.length - 1];

  // Local countdown: purely cosmetic; server PHASE_END is authoritative.
  const secondsLeft = useDeadlineCountdown(msRemaining);

  // Defensive fallback: no summary yet (snapshot arrived before the reducer
  // appended). Per PRD §3.9 reveal is always preceded by a PHASE_END that builds a
  // summary, so this shouldn't fire in production, but a fresh client connecting
  // *during* reveal might briefly land here.
  if (!summary) {
    return (
      <div
        class="reveal-phase-shell reveal-phase-shell--loading"
        data-testid="reveal-phase-shell"
        role="status"
        aria-live="polite"
      >
        <p>Adding up the points…</p>
      </div>
    );
  }

  const drawerName = players.find((p) => p.id === summary.drawerId)?.name ?? "The drawer";
  const abortText = abortedReasonText(summary.abortedReason);
  const guesses = summary.guesses;

  // Running CUMULATIVE standings (totals across ALL completed turns), same pure
  // helper as the gameOver board, departed-aware (Q9) so a mid-game leaver's
  // points don't vanish from the between-turns standings.
  const rosterMap = new Map(players.map((p) => [p.id, p]));
  const scoreboard = aggregateScoreboard(game.turnSummaries, rosterMap, departedNames ?? new Map());
  // Competition ranking so tied cumulative totals share a rank (1,1,3) instead
  // of the raw index (1,2,3): same bug/fix as the gameOver table.
  const standingsRanks = competitionRanks(scoreboard.map((e) => e.total));
  const scores = buildTurnScores(players, game.turnSummaries);

  return (
    <div
      class="drawing-phase-shell reveal-phase-shell"
      data-testid="reveal-phase-shell"
      data-phase="reveal"
      role="region"
      aria-label="Turn reveal"
    >
      {/* LEFT: the players panel STAYS visible (reveal no longer hides it). */}
      <aside class="drawing-phase-col drawing-phase-col--left" data-testid="drawing-phase-col-left">
        <PlayersPanel
          players={players}
          drawerId={summary.drawerId}
          myId={playerId}
          scores={scores}
          roomId={roomId}
        />
      </aside>

      {/* CENTER: the kept drawing at the SAME size as the drawing phase, with the
          reveal summary OVERLAID (opaque card on a paper scrim), never a swap. */}
      <div
        class="drawing-phase-col drawing-phase-col--center"
        data-testid="drawing-phase-col-center"
      >
        <div class="drawing-phase-header">
          <span class="drawing-phase-round" data-testid="drawing-phase-round">
            {headerLabel(game)}
          </span>
        </div>

        <div class="canvas-wrapper drawing-phase-canvas-wrapper reveal-phase-canvas-wrapper">
          {/* The kept drawing: inert, aria-hidden, BEHIND the scrim/card. */}
          {canvasSlot ? (
            <div
              class="reveal-phase-canvas-layer"
              data-testid="reveal-phase-canvas-layer"
              aria-hidden="true"
            >
              {canvasSlot}
            </div>
          ) : null}

          {/* The reveal summary: an opaque --paper card on a >=80% --paper scrim,
              centered over the canvas so the score text stays legible over the
              kept multi-color drawing (C6). */}
          <div class="reveal-phase-scrim" data-testid="reveal-phase-scrim">
            <div class="reveal-phase-card" data-testid="reveal-phase-card">
              <div class="reveal-phase-header reveal-phase-word-frame">
                <span
                  class={`reveal-phase-word-frame-bg ${SCRIBBLE_IN_CLASS}`}
                  data-testid="reveal-phase-word-frame"
                  aria-hidden="true"
                >
                  <BoxFrame width={240} height={88} stretch />
                </span>
                <p class="reveal-phase-word-label">The word was:</p>
                <p class="reveal-phase-word" data-testid="reveal-phase-word">
                  “{summary.word}”
                </p>
              </div>

              {/* Ceremony beats (audit 2026-06-11): the reveal used to render
                  everything at t=0 behind one 220ms fade, the exact
                  "simultaneous fade" the PRD forbids, on the marquee moment.
                  Beat 1 = the word (above, scribbles in at mount); beat 2 =
                  credit + drawer score; beat 3 = guesser rows, staggered;
                  beat 4 = standings, then the next-turn line. CountUps hold
                  (delayMs) so numbers never finish while still invisible. */}
              <div class="reveal-phase-drawer-line reveal-beat reveal-beat--credit">
                <span class="reveal-phase-drawer" data-testid="reveal-phase-drawer">
                  Drawn by <strong>{drawerName}</strong>
                </span>
                <span
                  class={`reveal-phase-tier reveal-phase-tier--${summary.tier}`}
                  data-testid={`reveal-phase-tier-${summary.tier}`}
                >
                  {summary.tier}
                </span>
              </div>

              <div
                class="reveal-phase-drawer-score reveal-beat reveal-beat--credit"
                data-testid="reveal-phase-drawer-score"
              >
                Drawer scored:{" "}
                <strong>
                  <CountUp value={summary.drawerPoints} delayMs={600} />
                </strong>{" "}
                pts
              </div>
              {/* One-line breakdown (audit 2026-06-11: naked numbers bred
                  folk rules: players believed speed mattered; it's order).
                  Suppressed on drawer-disconnect aborts (review P3): the
                  reducer forces 0 there regardless of guesses, so a formula
                  (or the zero-guess scold) under a forced 0 would be a lie,
                  abortText below carries the real explanation. */}
              {summary.abortedReason !== "drawer-disconnect" && (
                <p
                  class="reveal-phase-score-why reveal-beat reveal-beat--credit"
                  data-testid="reveal-phase-score-why"
                >
                  {guesses.length === 0
                    ? // Zero-guess gate (owner decision, audit 2026-06-11): the
                      // drawer scores nothing when nobody guesses, the copy
                      // states the rule so the 0 above reads as a rule, not a bug.
                      "Nobody guessed — no points. Gotta be guessable!"
                    : `250 base + 250 × ${guesses.length} guesser${guesses.length === 1 ? "" : "s"}, ${summary.tier} ×${DIFFICULTY_MULTIPLIER[summary.tier]}`}
                </p>
              )}

              {abortText && (
                <div
                  class="reveal-phase-abort reveal-beat reveal-beat--credit"
                  data-testid="reveal-phase-abort"
                >
                  {abortText}
                </div>
              )}

              <div class="reveal-phase-guesses reveal-beat reveal-beat--guessers">
                <p class="reveal-phase-guesses-label">Correct guessers:</p>
                {guesses.length === 0 ? (
                  <p class="reveal-phase-no-guesses" data-testid="reveal-phase-no-guesses">
                    Nobody got it. Tough crowd.
                  </p>
                ) : (
                  <ol class="reveal-phase-guesses-list" data-testid="reveal-phase-guesses-list">
                    {guesses.map((g, idx) => {
                      // Review fix (adversarial P3): a departed guesser used
                      // to render as a raw player id here while the standings
                      // two beats later showed their cached name "(left)".
                      const name =
                        players.find((p) => p.id === g.playerId)?.name ??
                        departedNames?.get(g.playerId) ??
                        "Someone";
                      return (
                        <li
                          class="reveal-phase-guess"
                          data-testid={`reveal-phase-guess-${g.order}`}
                          key={g.playerId}
                          style={{ "--row-i": idx }}
                        >
                          <span class="reveal-phase-guess-rank">{g.order}.</span>
                          <span class="reveal-phase-guess-name">{name}</span>
                          <span class="reveal-phase-guess-points">
                            <CountUp value={g.points} delayMs={1300 + idx * 130} /> pts
                          </span>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>

              {scoreboard.length > 0 && (
                <div class="reveal-phase-scoreboard-block reveal-beat reveal-beat--standings">
                  <p class="reveal-phase-scoreboard-label">Standings:</p>
                  <ol class="reveal-phase-scoreboard" data-testid="reveal-phase-scoreboard">
                    {scoreboard.map((entry, i) => (
                      <li
                        class={`reveal-phase-scoreboard-row${entry.left ? " reveal-phase-scoreboard-row--left" : ""}`}
                        data-testid={`reveal-phase-scoreboard-row-${i + 1}`}
                        key={entry.playerId}
                      >
                        <span class="reveal-phase-scoreboard-rank">{standingsRanks[i]}.</span>
                        <span class="reveal-phase-scoreboard-name">
                          {entry.name}
                          {entry.left ? " (left)" : ""}
                        </span>
                        <span class="reveal-phase-scoreboard-total">
                          <CountUp value={entry.total} delayMs={2000} /> pts
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              <p
                class="reveal-phase-timer reveal-beat reveal-beat--next"
                data-testid="reveal-phase-timer"
                aria-live="polite"
              >
                Next turn in {secondsLeft}s…
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT: the guess feed STAYS visible (reveal no longer hides it). */}
      <aside
        class="drawing-phase-col drawing-phase-col--right"
        data-testid="drawing-phase-col-right"
      >
        {/* Guessing is over at reveal: don't invite one (audit 2026-06-11). */}
        <GuessChatFeed chatLog={game.chatLog} emptyHint="No guesses this turn." />
      </aside>
    </div>
  );
}
